/**
 * Home Hub (07-hubs §3; launch contents per Jon 2026-11-03): greeting + date,
 * notifications (backup alerts from the Backup Monitor), recent Lupa chats,
 * quick links (pinned items, saved per account) and an Announcements placeholder (no backing data yet).
 * Front door (handoffs/09 §4, Jon 2026-11-03): "My work" (the person's week) and
 * "Team tools" (HubSpot, Teamwork, Ramp, BambooHR, Google Drive), in HomeMyWork.tsx.
 *
 * Layout (Jon, 2026-11-03, HubSpot-style): a main column (quick links, recent
 * chats) and a right-hand "Your day at a glance" panel with tabs
 * (Notifications, Announcements). Stacks to one column on narrow screens.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faBell, faComments, faLink, faBullhorn, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useLupaChat } from '../contexts/LupaChatContext';
import { DWA } from '../theme/dwaTokens';
import { canSeeItem, findHub, findItem, greetingName, HUB_ITEMS } from '../hubs/hubs';
import { ComingSoonPill, GradientPage, Panel } from '../hubs/HubCards';
import { HubLayout } from '../hubs/HubHeader';
import { readRememberedClient, usePinnedItems } from '../hubs/preferences';
import { canSeeDwaDashboard } from './dwaInternalAccess';
import { fetchBackupMonitor } from '../services/dashboardApi';
import type { BackupJob } from '../services/dashboardApi';
import { MyWorkPanel, TeamToolsPanel } from './HomeMyWork';

const BACKUP_SLUG = 'dwa-web-backup-monitor';

const rowLink: React.CSSProperties = {
  display: 'block',
  padding: '0.6rem 0.7rem',
  borderRadius: DWA.radius,
  textDecoration: 'none',
  color: DWA.ink,
  background: '#f6f6f4',
  fontSize: '0.875rem',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: DWA.font,
};

const muted: React.CSSProperties = { color: DWA.textSecondary, fontSize: '0.85rem', margin: 0, lineHeight: 1.5 };

type BackupState =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'loaded'; total: number; alerts: BackupJob[] };

const HEALTH_LABEL: Record<string, string> = {
  failed: 'Failed',
  incomplete: 'Incomplete',
  stale: 'Overdue',
};

const NotificationsPanel: React.FC = () => {
  const { user } = useAuth();
  const canSeeBackups = canSeeDwaDashboard(user, BACKUP_SLUG);
  const [state, setState] = useState<BackupState>(canSeeBackups ? { kind: 'loading' } : { kind: 'hidden' });

  useEffect(() => {
    if (!canSeeBackups) return;
    let cancelled = false;
    fetchBackupMonitor()
      .then((res) => {
        if (cancelled) return;
        setState({ kind: 'loaded', total: res.job_count, alerts: res.jobs.filter((j) => j.health !== 'ok') });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [canSeeBackups]);

  return (
    <div>
      {state.kind === 'hidden' && <p style={muted}>No notifications.</p>}
      {state.kind === 'loading' && <p style={muted}>Checking backups…</p>}
      {state.kind === 'error' && <p style={muted}>Couldn't load backup status right now.</p>}
      {state.kind === 'loaded' && state.alerts.length === 0 && (
        <p style={muted}>
          All {state.total} backup job{state.total === 1 ? '' : 's'} healthy.{' '}
          <Link to={`/dashboard/${BACKUP_SLUG}`} style={{ color: DWA.ink, fontWeight: 600 }}>
            Backup Monitor
          </Link>
        </p>
      )}
      {state.kind === 'loaded' && state.alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {state.alerts.map((job) => (
            <Link key={`${job.client}/${job.job}`} to={`/dashboard/${BACKUP_SLUG}`} style={rowLink}>
              <span style={{ fontWeight: 700, color: job.health === 'stale' ? '#92400e' : DWA.danger }}>
                Backup {HEALTH_LABEL[job.health] ?? job.health}
              </span>{' '}
              · {job.client_name}
              {job.reasons[0] && (
                <span style={{ display: 'block', fontSize: '0.78rem', color: DWA.textSecondary }}>{job.reasons[0]}</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const RecentChatsPanel: React.FC = () => {
  const { conversations, conversationsLoaded, selectConversation } = useLupaChat();
  const navigate = useNavigate();
  const recent = useMemo(
    () => conversations.filter((c) => !c.isArchived && c.messages.length > 0).slice(0, 5),
    [conversations]
  );

  return (
    <Panel
      icon={faComments}
      title="Recent Lupa chats"
      action={
        <Link to="/lupa" style={{ fontSize: '0.8rem', color: DWA.goldInk, fontWeight: 700 }}>
          Open Lupa
        </Link>
      }
    >
      {!conversationsLoaded && <p style={muted}>Loading…</p>}
      {conversationsLoaded && recent.length === 0 && <p style={muted}>No chats yet. Ask Lupa anything.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {recent.map((c) => (
          <button
            key={c.id}
            type="button"
            style={rowLink}
            onClick={() => {
              selectConversation(c.id);
              navigate('/lupa');
            }}
          >
            <span style={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.title}
            </span>
            <span style={{ fontSize: '0.75rem', color: DWA.textMuted }}>
              {c.updatedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </button>
        ))}
      </div>
    </Panel>
  );
};

/** Default quick links before the user pins anything. */
const DEFAULT_QUICK_LINKS = ['dwa-accounts-client-database', 'dwa-web-backup-monitor', 'dwa-creative-scorecard'];

const smallButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '0.2rem 0.35rem',
  cursor: 'pointer',
  color: DWA.textSecondary,
  fontFamily: DWA.font,
  fontSize: '0.8rem',
};

/**
 * The person's own links (pinned Hub items), saved to their account. Edit adds,
 * removes and reorders; the pin button on any page adds too. Before anything is
 * pinned it shows suggestions, which Edit turns into the person's own list.
 */
export const QuickLinksPanel: React.FC = () => {
  const { user } = useAuth();
  const { pins, setPinOrder } = usePinnedItems(user?.id);
  const [editing, setEditing] = useState(false);

  const visible = useCallback((id: string) => {
    const item = findItem(id);
    return Boolean(item && item.route && canSeeItem(user, item));
  }, [user]);

  const suggested = useMemo(() => {
    const ids = [...DEFAULT_QUICK_LINKS];
    if (user?.id) {
      // Remembered client → its CRM dashboard (07-hubs §2 rule 5).
      const client = readRememberedClient(user.id);
      const crm = HUB_ITEMS.find((i) => i.clientId && i.clientId === client);
      if (crm) ids.unshift(crm.id);
    }
    return ids.filter(visible);
  }, [user, visible]);

  const ids = pins.length > 0 ? pins.filter(visible) : suggested;
  const items = ids.map((id) => findItem(id)!).filter(Boolean);
  const addable = HUB_ITEMS.filter((i) => i.route && canSeeItem(user, i) && !ids.includes(i.id)).sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  const move = (index: number, delta: number) => {
    const next = [...ids];
    const [moved] = next.splice(index, 1);
    next.splice(index + delta, 0, moved);
    setPinOrder(next);
  };

  return (
    <Panel
      icon={faLink}
      title="Quick links"
      action={
        <button type="button" style={{ ...smallButton, color: DWA.goldInk, fontWeight: 700 }} onClick={() => setEditing((v) => !v)}>
          {editing ? 'Done' : 'Edit'}
        </button>
      }
    >
      {items.length === 0 && <p style={muted}>No quick links yet. Use Edit, or the pin button on any page.</p>}
      <div data-testid="home-quick-links" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {items.map((item, index) =>
          editing ? (
            <div key={item.id} style={{ ...rowLink, display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'default' }}>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{item.title}</span>
              <button type="button" style={smallButton} aria-label={`Move ${item.title} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
              <button
                type="button"
                style={smallButton}
                aria-label={`Move ${item.title} down`}
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </button>
              <button type="button" style={smallButton} aria-label={`Remove ${item.title}`} onClick={() => setPinOrder(ids.filter((p) => p !== item.id))}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ) : (
            <Link key={item.id} to={item.route ?? '/'} style={rowLink}>
              <span style={{ fontWeight: 600 }}>{item.title}</span>
              <span style={{ fontSize: '0.75rem', color: DWA.textMuted }}> · {findHub(item.home)?.title}</span>
            </Link>
          ),
        )}
      </div>
      {editing && addable.length > 0 && (
        <select
          aria-label="Add a quick link"
          value=""
          onChange={(e) => e.target.value && setPinOrder([...ids, e.target.value])}
          style={{ marginTop: '0.6rem', width: '100%', padding: '0.5rem', borderRadius: DWA.radius, border: `1px solid ${DWA.lineLight}`, fontFamily: DWA.font }}
        >
          <option value="">+ Add a link…</option>
          {addable.map((i) => (
            <option key={i.id} value={i.id}>
              {i.title} ({findHub(i.home)?.title})
            </option>
          ))}
        </select>
      )}
      {!editing && pins.length === 0 && (
        <p style={{ ...muted, fontSize: '0.75rem', marginTop: '0.75rem' }}>
          Suggestions. Use Edit, or the pin button on any page, to make this list your own. It's saved to your account.
        </p>
      )}
    </Panel>
  );
};

type GlanceTab = 'notifications' | 'announcements';

const GLANCE_TABS: { id: GlanceTab; label: string; icon: typeof faBell }[] = [
  { id: 'notifications', label: 'Notifications', icon: faBell },
  { id: 'announcements', label: 'Announcements', icon: faBullhorn },
];

/** Right-hand "Your day at a glance" panel with tabs (arrow keys move between tabs). */
const AtAGlance: React.FC = () => {
  const [tab, setTab] = useState<GlanceTab>('notifications');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (e: React.KeyboardEvent) => {
    const i = GLANCE_TABS.findIndex((t) => t.id === tab);
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % GLANCE_TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + GLANCE_TABS.length) % GLANCE_TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = GLANCE_TABS.length - 1;
    else return;
    e.preventDefault();
    const id = GLANCE_TABS[next].id;
    setTab(id);
    tabRefs.current[id]?.focus();
  };

  return (
    <aside
      aria-labelledby="glance-title"
      data-testid="home-at-a-glance"
      style={{ background: '#ffffff', border: `1px solid ${DWA.lineLight}`, borderRadius: DWA.radius, minWidth: 0, overflow: 'hidden' }}
    >
      <h2 id="glance-title" style={{ margin: 0, padding: '1rem 1.1rem 0.5rem', fontSize: '1rem', fontWeight: 700, color: DWA.ink }}>
        Your day at a glance
      </h2>
      <div role="tablist" aria-label="At a glance" onKeyDown={onKeyDown} style={{ display: 'flex', gap: '0.25rem', padding: '0 0.75rem', borderBottom: `1px solid ${DWA.lineLight}` }}>
        {GLANCE_TABS.map((t) => (
          <button
            key={t.id}
            ref={(el) => {
              tabRefs.current[t.id] = el;
            }}
            type="button"
            role="tab"
            id={`glance-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`glance-panel-${t.id}`}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.6rem 0.5rem',
              border: 'none',
              borderBottom: `3px solid ${tab === t.id ? DWA.gold : 'transparent'}`,
              background: 'none',
              color: tab === t.id ? DWA.ink : DWA.textSecondary,
              fontWeight: tab === t.id ? 700 : 500,
              fontSize: '0.85rem',
              cursor: 'pointer',
              fontFamily: DWA.font,
            }}
          >
            <FontAwesomeIcon icon={t.icon} aria-hidden="true" style={{ fontSize: '0.75rem' }} />
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`glance-panel-${tab}`} aria-labelledby={`glance-tab-${tab}`} style={{ padding: '1rem 1.1rem 1.1rem' }}>
        {tab === 'notifications' ? (
          <NotificationsPanel />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
            <ComingSoonPill />
            <p style={muted}>Agency news and updates will show here.</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export const HomeHubPage: React.FC = () => {
  const { user } = useAuth();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <HubLayout>
      <GradientPage>
        <main style={{ maxWidth: 1200, margin: '0 auto', padding: '1.5rem clamp(1rem, 3vw, 1.75rem) 6rem' }}>
          <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.25rem', color: '#ffffff' }}>
            Hi, {greetingName(user)}
          </h1>
          <p style={{ margin: '0 0 1.5rem', color: DWA.onDarkMuted }}>{today}</p>

          <div className="ow-home-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 0 }}>
              <MyWorkPanel />
              <QuickLinksPanel />
              <TeamToolsPanel />
              <RecentChatsPanel />
            </div>
            <AtAGlance />
          </div>
        </main>
      </GradientPage>
    </HubLayout>
  );
};

export default HomeHubPage;
